import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { browser } from '@dc/ui-library/src/helpers/browser';
import { debounce } from '@codex-brightworld/helpers';
import { Icon } from '@codex-brightworld/icons';
import ClosableTooltip from '@dc/ui-library/src/organisms/ClosableTooltip/ClosableTooltip';
import ProductSwatchTooltip from '../ProductSwatchTooltip/ProductSwatchTooltip';

const whatInput = typeof window !== 'undefined'
  ? require('what-input')
  : {
    ask: () => undefined
  };

const InfoToolTipIcon = styled(Icon)`
  cursor: pointer;
  &.dc-icon {
    margin-left: 4px;
    stroke: none;
    width: 24px;
    height: 24px;
  }
  svg {
    width: 24px;
    height: 24px;
  }
  svg path:last-child {
    fill: ${({ theme }) => theme.colors.primary.purple};
  }
`;

const ProductSwatchesDiv = styled.div`
  margin-bottom: 10px;
`;

const ProductSwatchesTitle = styled.div`
  margin: 0 0 10px 0;
  display: block;
  font-family: ${({ theme }) => theme.fonts.default.fontFamily};
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.neutral.currysBlack};
  position: relative;

  .dc-tooltip {
    width: ${({ browser }) => (browser.isWide ? '270px' : '250px')};
    left: ${({ browser }) => (browser.isWide || browser.isDesktop ? '20px' : '5px')};
    top: ${({ browser }) => (browser.isTouch ? '33px' : '-43px')};
    margin: 0;
    border-radius: 5% 5% 5% 5%;

    &:before {
      ${({ browser }) => (browser.isWide || browser.isDesktop ? 'top: 38px' : '')};
      ${({ browser }) => (browser.isTablet ? 'top: -11px' : '')};
      ${({ browser }) => (browser.isMobile ? 'top: -9px' : '')};
      ${({ browser }) => (browser.isTouch ? 'left: 30px;' : '')};
      background-color: ${({ theme }) => theme.colors.neutral.white};
      border-width: 1px 1px 0 0;
      border-style: solid;
      border-color: ${({ theme }) => theme.colors.neutral.grey[3]};
      box-shadow: 1px -1px 0px ${({ theme }) => theme.colors.neutral.grey[3]};;
    }
  }

  .dc-tooltip-info {
    background-color: ${({ theme }) => theme.colors.neutral.white};
    border: 1px solid ${({ theme }) => theme.colors.semantic.lightBlue};
    box-shadow: 0px 0px 5px ${({ theme }) => theme.colors.neutral.grey[3]};;
  }

  .dc-tooltip-info a:link {
    color: ${({ theme }) => theme.colors.primary.purpleHover};;
  }

  .dc-tooltip-info a:hover {
    text-decoration: underline;
  }
  
  .dc-close-label a:link {
    color: ${({ theme }) => theme.colors.primary.purpleHover};    
  }

  .dc-product-swatch-closable-tooltip {
    color: ${({ theme }) => theme.colors.primary.purpleHover};
    
    &.dc-icon {
      width: 16px;
      height: 16px;
    }
    svg {
      fill: ${({ theme }) => theme.colors.primary.purpleHover};   
    }
  }
`;

const ToolTipWrapper = styled.div`
  position: ${({ browser }) => (browser.isWide || browser.isDesktop ? 'relative' : 'unset')};
  display: inline-block;
`;

const HelpTooltip = styled(ClosableTooltip)`
`;

const ProductSwatch = styled.li`
  position: relative;
  display: inline-block;
  margin: 0 10px 5px 0;
  vertical-align: top;
  width: 75px;
  text-align: center;
`;

const ProductSwatchLink = styled.a`
  display: inline-block;
  text-align: center;
`;

const ProductSwatchImg = styled.img`
  display: block;
  width: ${({ isColour }) => (isColour ? '52px' : '34px')};
  height: ${({ isColour }) => (isColour ? '52px' : '34px')};
  margin: 0 auto 5px auto;
  border: ${({ isActive }) => (isActive ? '2px' : '1px')}
    solid  ${({ isActive, theme }) => (isActive ? theme.colors.primary.purple : theme.colors.neutral.grey[2])};
  cursor: ${({ isActive }) => (isActive ? 'default' : 'pointer')};
  border-radius: 50%;
  padding: ${({ isColour }) => (isColour ? '0px' : '9px')};
`;

const ProductSwatchValue = styled.span`
  font-family: ${({ theme }) => theme.fonts.default.fontFamily};
  text-decoration: none;
  color: ${({ isActive, theme }) => (isActive ? theme.colors.primary.purple : theme.colors.neutral.currysBlack)};
  display: inline-block;
  word-break: break-word;
  font-size: 13px;
  font-weight: bolder;
  line-height: 1.38;
`;

class ProductSwatches extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      swatchTooltipFupid: null, // tooltip specific for every swatch (eg. green, red, blue color)
      swatchHelpTooltipArrow: browser().isTouch ? 'top' : 'left'
    };

    this.renderSwatch = this.renderSwatch.bind(this);
    this.onSwatchClick = this.onSwatchClick.bind(this);
    this.onSwatchMouseOver = this.onSwatchMouseOver.bind(this);
    this.onSwatchMouseLeave = this.onSwatchMouseLeave.bind(this);
    this.onHelpIconClick = this.onHelpIconClick.bind(this);
    this.closeSwatchHelpTooltip = this.closeSwatchHelpTooltip.bind(this);
  }

  // eslint-disable-next-line react/no-deprecated
  componentWillMount() {
    window.addEventListener('resize', debounce(() => {
      this.setState({
        swatchHelpTooltipArrow: browser().isTouch ? 'top' : 'left'
      });
    }, 200));
  }

  onSwatchClick(swatchData) {
    if (this.props.onSwatchClick) {
      this.props.onSwatchClick(
        this.props.title.toLocaleLowerCase(),
        this.props.variants.length,
        swatchData
      );
    }
  }

  onSwatchMouseOver(variantFupid) {
    if (whatInput.ask() === 'touch') {
      return;
    }
    const browserType = browser();
    if (browserType.isWide || browserType.isDesktop) {
      this.setState({
        swatchTooltipFupid: variantFupid
      });
    }
  }

  onSwatchMouseLeave() {
    this.setState({
      swatchTooltipFupid: null
    });
  }

  onHelpIconClick() {
    this.props.onChangeHelpTooltipVisibility(this.props.title, true);
  }

  closeSwatchHelpTooltip() {
    this.props.onChangeHelpTooltipVisibility(this.props.title, false);
  }

  renderSwatch(swatchData) {
    const isColour = swatchData.swatch_img_url.includes('colour-swatch');
    const isActive = swatchData.fupid === this.props.current_product_fupid;
    return (
      <ProductSwatch
        onMouseOver={(e) => this.onSwatchMouseOver(swatchData.fupid)}
        onMouseLeave={this.onSwatchMouseLeave}
        onClick={(e) => this.onSwatchClick(swatchData)}
        key={swatchData.fupid}
        data-interaction={this.props.dataInteraction}
        isActive={isActive}
      >
        <ProductSwatchLink
          href={isActive ? null : swatchData.product_url}
        >
          <ProductSwatchImg
            alt={''}
            src={swatchData.swatch_img_url}
            isActive={isActive}
            isColour={isColour}
          />
          <ProductSwatchValue
            isActive={isActive}
            dangerouslySetInnerHTML={{ __html: swatchData.variant }}
          />
        </ProductSwatchLink>

        {this.state.swatchTooltipFupid === swatchData.fupid &&
          [
            <div
              className={'dc-product-tooltip-hover-bridge'}
              key={`bridge-${swatchData.fupid}`}
            >
              <ProductSwatchTooltip
                product={swatchData}
                outOfStockTranslate={this.props.outOfStockTranslate}
                priceSaveTranslate={this.props.priceSaveTranslate}
                emailMeWhenBackInStockTranslate={this.props.emailMeWhenBackInStockTranslate}
                key={`tooltip-${swatchData.fupid}`}
              />
            </div>
          ]}
      </ProductSwatch>
    );
  }

  render() {
    return (
      <ProductSwatchesDiv data-component={'ProductSwatches'}>
        <ProductSwatchesTitle browser={browser()}>
          {this.props.swatch_title}
          {this.props.swatch_help_tooltip_translate &&
          !!this.props.swatch_help_tooltip_translate.length && (
            <InfoToolTipIcon
              icon={'info_tooltip'}
              onClick={this.onHelpIconClick}
            />
          )}
          <ToolTipWrapper browser={browser()}>
            <HelpTooltip
              arrow={this.state.swatchHelpTooltipArrow}
              closeButtonClasses={'dc-product-swatch-closable-tooltip'}
              floating
              display={this.props.getHelpTooltipVisibility(this.props.title)}
              type={'info'}
              style={'compact'}
              onCloseButtonClick={this.closeSwatchHelpTooltip}
            >
              <span
                dangerouslySetInnerHTML={{ __html: this.props.swatch_help_tooltip_translate }}
              />
            </HelpTooltip>
          </ToolTipWrapper>
        </ProductSwatchesTitle>
        <ul className={'dc-product-swatches-list'}>
          {this.props.variants.map(this.renderSwatch)}
        </ul>
      </ProductSwatchesDiv>
    );
  }
}

ProductSwatches.propTypes = {
  current_product_fupid: PropTypes.string.isRequired,
  current_product_variant: PropTypes.string.isRequired,
  isHelpTooltipVisible: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  swatch_title: PropTypes.string.isRequired,
  variants: PropTypes.arrayOf(
    PropTypes.shape({
      fupid: PropTypes.string,
      img_url: PropTypes.string,
      out_of_stock: PropTypes.bool,
      price: PropTypes.string,
      was_price: PropTypes.string,
      product_url: PropTypes.string,
      saving: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.string
      ]),
      swatch_img_url: PropTypes.string,
      variant: PropTypes.string
    })
  ),
  swatch_help_tooltip_translate: PropTypes.string,
  onSwatchClick: PropTypes.func,
  onChangeHelpTooltipVisibility: PropTypes.func,
  getHelpTooltipVisibility: PropTypes.func,
  priceSaveTranslate: PropTypes.string,
  outOfStockTranslate: PropTypes.string,
  emailMeWhenBackInStockTranslate: PropTypes.string,
  dataInteraction: PropTypes.string
};

ProductSwatches.defaultProps = {
  variants: [],
  onSwatchClick: () => {},
  onChangeHelpTooltipVisibility: () => {},
  getHelpTooltipVisibility: () => {},
  emailMeWhenBackInStockTranslate: 'Email me when back in stock',
  priceSaveTranslate: 'Save',
  outOfStockTranslate: 'Out of stock',
  swatch_help_tooltip_translate: '',
  dataInteraction: undefined
};

ProductSwatches.displayName = 'ProductSwatches';

export default ProductSwatches;
